import useEventListener from "@/hooks/useEventListener";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { cn } from "@/libraries/cn";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchIcon from "@mui/icons-material/Search";
import FormControl, { type FormControlProps } from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput from "@mui/material/OutlinedInput";
import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface FloatingSearchProps
	extends Omit<FormControlProps, "onChange" | "onBlur"> {
	name?: string;
	value?: unknown;
	placeholder?: string;
	onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	onClear?: () => void;
	inputClassName?: string;
}

const FloatingSearchV2: React.FC<FloatingSearchProps> = (props) => {
	const { t } = useTranslation("common");
	const {
		name,
		value,
		placeholder,
		onChange,
		onBlur,
		onClear,
		className,
		children,
		inputClassName,
		...formControlProps
	} = props;
	const refSearch = useRef<HTMLDivElement>(null);
	const refInput = useRef<HTMLInputElement>(null);
	const [isExpanded, setIsExpanded] = useState(!!value);

	useOnClickOutside(refSearch, () => {
		if (value) return;
		setIsExpanded(false);
	});

	useEventListener(
		"click",
		() => {
			setIsExpanded(true);
		},
		refSearch,
	);

	return (
		<>
			{!isExpanded && children ? children : null}
			<FormControl
				ref={refSearch}
				focused={isExpanded}
				variant="outlined"
				size="small"
				fullWidth
				className={cn(
					"!rounded-full !overflow-hidden !transition-all !duration-500 !ease-in-out",
					{
						"!w-10 !cursor-pointer !bg-transparent [&_fieldset]:!border-none":
							!isExpanded,
						"!flex-1 !w-full !shadow-md !bg-white": isExpanded,
					},
					className,
				)}
				{...formControlProps}
			>
				<OutlinedInput
					ref={refInput}
					name={name}
					placeholder={placeholder ?? t("search")}
					aria-label="search"
					className={cn("!rounded-full", inputClassName)}
					startAdornment={
						<InputAdornment position="start">
							<SearchIcon
								className={cn("text-zinc-950", {
									"-translate-x-1": !isExpanded,
									"!fill-black": isExpanded,
								})}
							/>
						</InputAdornment>
					}
					endAdornment={
						!value ? null : (
							<InputAdornment position="end">
								<IconButton onClick={onClear}>
									<CloseRoundedIcon className="text-black !fill-black" />
								</IconButton>
							</InputAdornment>
						)
					}
					value={value}
					onChange={onChange}
					onBlur={onBlur}
				/>
			</FormControl>
		</>
	);
};

export default FloatingSearchV2;
